/** The parts of a plan's details (/subscription/plandetail) that pricing reads. */
export type PlanPricing = {
  /** Base price per billing term. */
  price: Record<string, number | number[]>
  /** Add-on code (e.g. "add_on_social") keyed by add-on slot. */
  addOns: Record<string, string>
  /** Per-pack prices, keyed by add-on slot then billing term. */
  addOnPrices: Record<string, Record<string, number>>
}

/** Pack counts keyed by add-on code. */
export type AddOnQuantities = Partial<Record<string, number>>

/**
 * A plan's list price for one billing term, add-on packs included. Coupons and
 * grandfathered pack prices are deliberately left out: this is what the plan
 * costs, which is what the billing-term dialogs and the annual nudge compare.
 */
export function listPrice(plan: PlanPricing, term: number, quantities: AddOnQuantities = {}): number {
  let price = Number(plan.price[term]) || 0

  for (const [slot, code] of Object.entries(plan.addOns ?? {})) {
    price += (quantities[code] ?? 0) * (Number(plan.addOnPrices?.[slot]?.[term]) || 0)
  }

  return price
}

/** What paying annually saves over twelve monthly bills, at list price. */
export function annualSavings(plan: PlanPricing, monthlyTerm: number, annualTerm: number, quantities: AddOnQuantities = {}) {
  const monthly = listPrice(plan, monthlyTerm, quantities)
  const annual = listPrice(plan, annualTerm, quantities)
  const savings = monthly * 12 - annual

  return {
    monthly,
    annual,
    savings,
    pct: monthly > 0 ? Math.round((savings / (monthly * 12)) * 100) : 0,
  }
}

/**
 * What an upgrade to Plus or Agency+ adds, in sentence form for the upgrade
 * dialog. The same sets the plan page's upgrade banner sells.
 */
export function upgradeFeatures(constants: Record<string, any>, level: number): string[] | null {
  switch (level) {
    case constants.PUB_LEVEL_9:
      return ["post recycler", "bulk import", "smart hashtags"]
    case constants.PUB_LEVEL_10:
      return ["EverQueue", "categories", "smart hashtags"]
    default:
      return null
  }
}

/** "a, b and c" */
export function listJoin(items: string[]): string {
  return items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`
}

/** Plan limits use -1 for no limit; the monthly post limit uses 0. */
export function limitLabel(value: number | string, format: (n: number) => string, unlimited = -1): string {
  const n = Number(value)

  return n === unlimited ? "Unlimited" : format(n)
}
